/**
 * Evidence sub-agent citation resolution: turns an answer's accumulated text +
 * a per-child `CitationRegistry` into `<cite-badge data-idx="N">` markers (that
 * the Streamdown handler renders as `CitationBadge`) plus the ordered
 * `ResolvedCitationOccurrence[]` driving the reference list. `buildImageMarkers`
 * does the same for `<img>TOKEN</img>` anchors against the image registry.
 *
 * Must operate on ACCUMULATED text, never deltas — a `<cite>` tag can split
 * across streamed chunks, so a dangling half-streamed fragment is stripped
 * rather than rendered. Pure + deterministic: streaming and reload hydration
 * share this module.
 */

import type { EnrichedReference, SimpleCitation } from '@heidi/evidence/contracts';
import { enrichCitations, getCitationKey } from '@heidi/evidence/contracts';
import type {
  CitationCandidateV1,
  CitationRegistry,
  EvidenceAgentImage,
  ResolvedCitationOccurrence,
} from '../../../../../contracts';
import { resolveTokenSource, toSimpleCitation } from './citation-model';

/**
 * Drop complete Evidence `<cite>` / `<img>` pairs from copied prose. A dangling
 * fragment is half a stream, so only a streaming caller strips it.
 */
const stripTagPairs = (
  text: string,
  tag: 'cite' | 'img',
  opts?: { streaming?: boolean }
): string => {
  const withoutComplete = text.replace(
    new RegExp(`<${tag}>[\\s\\S]*?</${tag}>`, 'g'),
    ''
  );
  return opts?.streaming
    ? withoutComplete.replace(new RegExp(`<${tag}>(?:(?!</${tag}>)[\\s\\S])*$`), '')
    : withoutComplete;
};

export const stripCiteTags = (text: string, opts?: { streaming?: boolean }): string =>
  stripTagPairs(text, 'cite', opts);

export const stripImgTags = (text: string, opts?: { streaming?: boolean }): string =>
  stripTagPairs(text, 'img', opts);

export interface CiteMarkerResult {
  /** Answer text with resolved `<cite>` tags replaced by `<cite-badge>` markers. */
  fullText: string;
  /** data-idx → single-element `SimpleCitation[]` for the `cite-badge` handler. */
  citationMap: Map<number, SimpleCitation[]>;
  /** Resolved occurrences in answer order (drives the reference list). */
  occurrences: ResolvedCitationOccurrence[];
  /** Tokens present but unresolvable — the caller reports these once, post-turn. */
  unresolvedTokens: string[];
}

// A complete `<cite>…</cite>` pair (non-greedy). Tokens are opaque (W/L/P prefix
// + hex) but we don't constrain the shape — validation is the registry's job.
const COMPLETE_CITE = /<cite>([\s\S]*?)<\/cite>/g;
// A trailing, still-streaming `<cite…` with no closing tag yet.
const DANGLING_CITE = /<cite>(?:(?!<\/cite>)[\s\S])*$/;

/**
 * Convert `<cite>TOKEN</cite>` → `<cite-badge>` markers + build the resolution
 * result. Re-run whenever the registry changes so late-arriving sources resolve.
 * `baseIdx` continues badge numbering across an answer split into multiple
 * `assistant_text` blocks — pass the count of occurrences already rendered
 * above this block.
 */
export const buildCiteMarkers = (
  text: string,
  registry: CitationRegistry,
  baseIdx = 0,
  opts?: { streaming?: boolean }
): CiteMarkerResult => {
  // A dangling `<cite…` fragment means "still streaming" mid-turn; on settled
  // text it's a literal unclosed tag, and stripping it would delete the rest
  // of a completed message.
  const withoutDangling = opts?.streaming ? text.replace(DANGLING_CITE, '') : text;
  const citationMap = new Map<number, SimpleCitation[]>();
  const occurrences: ResolvedCitationOccurrence[] = [];
  const unresolvedTokens: string[] = [];

  let out = '';
  let lastEnd = 0;
  let markerIdx = baseIdx;

  COMPLETE_CITE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = COMPLETE_CITE.exec(withoutDangling)) !== null) {
    const token = (match[1] ?? '').trim();
    // Text since the previous cite tag — used verbatim in the output AND as the
    // claim window (naturally excludes earlier cite tags).
    const preceding = withoutDangling.slice(lastEnd, match.index);
    out += preceding;

    const resolved = resolveTokenSource(registry, token);
    if (resolved) {
      const claimText = extractClaim(preceding);
      const selectedCandidate = selectCandidate(claimText, resolved.candidates);
      occurrences.push({
        token,
        sourceId: resolved.sourceId,
        source: resolved.source,
        selectedCandidate,
        claimText,
        index: markerIdx,
      });
      citationMap.set(markerIdx, [toSimpleCitation(resolved.source, selectedCandidate)]);
      out += `<cite-badge data-idx="${markerIdx}"></cite-badge>`;
      markerIdx += 1;
    } else if (token.length > 0) {
      unresolvedTokens.push(token);
      // Degrade to plain text: drop the marker, keep surrounding prose.
    }

    lastEnd = match.index + match[0].length;
  }
  out += withoutDangling.slice(lastEnd);

  return { fullText: out, citationMap, occurrences, unresolvedTokens };
};

// ── Image anchors ───────────────────────────────────────────────────────────

export interface ImageMarkerResult {
  /** Answer text with resolved `<img>` tags replaced by `<img-figure>` markers. */
  fullText: string;
  /** `image_id`s actually anchored — the caller omits these from the trailing strip. */
  placedImageIds: Set<string>;
  /** Anchors placed in this text — pass as the next block's `baseIdx` so display numbering continues. */
  placedCount: number;
  /** Tokens present but absent from the registry — the caller reports these once, post-turn. */
  unknownImageTokens: string[];
}

// Literal `<img>`/`</img>` only: `<img ` (a real attributed image tag) and the
// `<img-figure>` markers emitted below both fail the `>`-after-`img` requirement.
const COMPLETE_IMG = /<img>([\s\S]*?)<\/img>/g;
const DANGLING_IMG = /<img>(?:(?!<\/img>)[\s\S])*$/;

// Tokens are model-emitted, so escape before embedding one in an attribute.
const escapeAttribute = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/**
 * Convert `<img>TOKEN</img>` → `<img-figure data-token="TOKEN" data-n="N">`
 * markers (which the Streamdown handler renders through the legacy
 * `EvidenceImageBlock`, so sub-agent inline images look identical to the
 * legacy chat's). `data-n` is the 1-based display number, continued across a
 * multi-block answer via `baseIdx` — exactly like `buildCiteMarkers`. Runs
 * AFTER `buildCiteMarkers` on its marked text — the two tag sets are disjoint,
 * so cite-badge markers pass through untouched. An unknown token is removed
 * entirely, keeping the surrounding prose.
 *
 * Tag hygiene is UNCONDITIONAL: `<img>` is a real HTML tag, so a raw token pair
 * must never reach Streamdown even with the feature off. Callers achieve that by
 * always running this function and passing an EMPTY registry when the flag is
 * off — every tag is then removed and no figure is placed.
 */
export const buildImageMarkers = (
  text: string,
  imageRegistry: ReadonlyMap<string, EvidenceAgentImage>,
  baseIdx = 0,
  opts?: { streaming?: boolean }
): ImageMarkerResult => {
  // Same reasoning as `buildCiteMarkers`: a dangling `<img…` fragment is a
  // half-streamed tag mid-turn, but a literal unclosed tag once settled — where
  // stripping it would delete the rest of a completed message.
  const withoutDangling = opts?.streaming ? text.replace(DANGLING_IMG, '') : text;
  const placedImageIds = new Set<string>();
  const unknownImageTokens: string[] = [];

  let out = '';
  let lastEnd = 0;
  let placedCount = 0;

  COMPLETE_IMG.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = COMPLETE_IMG.exec(withoutDangling)) !== null) {
    const token = (match[1] ?? '').trim();
    out += withoutDangling.slice(lastEnd, match.index);

    if (imageRegistry.has(token)) {
      placedImageIds.add(token);
      placedCount += 1;
      const displayN = baseIdx + placedCount;
      out += `<img-figure data-token="${escapeAttribute(token)}" data-n="${displayN}"></img-figure>`;
    } else if (token.length > 0) {
      unknownImageTokens.push(token);
      // Degrade to nothing: drop the anchor, keep surrounding prose.
    }

    lastEnd = match.index + match[0].length;
  }
  out += withoutDangling.slice(lastEnd);

  return { fullText: out, placedImageIds, placedCount, unknownImageTokens };
};

/**
 * Deterministic reference list: traverse occurrences in answer order, dedupe by
 * `getCitationKey` (source_id-preferred — the same key badges/scroll use), then
 * enrich via the shared `enrichCitations`. The LLM never authors this list.
 */
export const buildReferences = (
  occurrences: readonly ResolvedCitationOccurrence[]
): EnrichedReference[] => {
  const seen = new Set<string>();
  const citations: SimpleCitation[] = [];
  for (const occ of occurrences) {
    const citation = toSimpleCitation(occ.source, occ.selectedCandidate);
    const key = getCitationKey(citation);
    if (seen.has(key)) continue;
    seen.add(key);
    citations.push(citation);
  }
  return enrichCitations(citations);
};

// ── Claim extraction ────────────────────────────────────────────────────────

/**
 * The clause/sentence immediately preceding the citation: strip markdown + XML,
 * take the text after the last sentence/list/paragraph boundary, cap at 500
 * chars (keep the tail nearest the citation). Earlier cite tags are already
 * excluded (caller passes only the window since the previous tag).
 */
export const extractClaim = (precedingText: string): string => {
  // Only strip plausible tags (`</?letter…>`) — the naive `<[^>]*>` also ate
  // clinical comparator spans like "target <140/90 … risk >25%".
  const withoutTagsAndLinks = precedingText
    .replace(/<\/?[a-zA-Z][^>]*>/g, ' ') // strip XML/HTML/custom tags
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1'); // md link → link text

  const boundary = /[.!?]["')\]]?\s|\n\s*(?:[-*+]|\d+[.)])\s|\n\s*\n/g;
  let start = 0;
  let m: RegExpExecArray | null;
  // Boundary detection runs BEFORE inline-md stripping so a `*`-bullet marker
  // can still terminate the claim window (stripping `*` first hid every bullet from it).
  while ((m = boundary.exec(withoutTagsAndLinks)) !== null) start = m.index + m[0].length;

  let claim = withoutTagsAndLinks
    .slice(start)
    .replace(/[*_`~#>|]/g, ' ') // inline md syntax
    .replace(/\s+/g, ' ')
    .trim();
  if (claim.length > 500) claim = claim.slice(claim.length - 500).trim();
  return claim;
};

// ── Claim-aware candidate selection ─────────────────────────────────────────

interface NormalizedTerms {
  terms: Set<string>;
  numbers: Set<string>;
  words: string[];
}

// Common English function words removed before scoring. Clinical numbers/units
// are retained (below), so this stays a plain stopword list.
const STOPWORDS = new Set([
  'the',
  'and',
  'for',
  'are',
  'was',
  'were',
  'with',
  'that',
  'this',
  'from',
  'has',
  'have',
  'had',
  'not',
  'but',
  'you',
  'your',
  'they',
  'their',
  'them',
  'can',
  'may',
  'will',
  'would',
  'should',
  'could',
  'been',
  'its',
  'into',
  'than',
  'then',
  'these',
  'those',
  'such',
  'which',
  'while',
  'also',
  'who',
  'whom',
  'what',
  'when',
  'where',
  'how',
  'why',
  'per',
  'via',
  'due',
]);

// 2-char clinical units are shorter than the ≥3 threshold but carry meaning.
const UNITS = new Set([
  'mg',
  'ml',
  'kg',
  'mm',
  'cm',
  'iu',
  'hr',
  'hz',
  'ng',
  'mcg',
  'mmol',
  'mmhg',
  'bpm',
  'gtt',
  'meq',
  'kpa',
  'min',
  'sec',
  'day',
  'wk',
]);

const hasDigit = (token: string): boolean => /\d/.test(token);

const normalizeTerms = (text: string): NormalizedTerms => {
  const raw = text.toLowerCase().match(/[a-z0-9][a-z0-9./-]*/g) ?? [];
  const terms = new Set<string>();
  const numbers = new Set<string>();
  const words: string[] = [];
  for (const rawToken of raw) {
    // `.`/`/`/`-` are valid continuation chars above, so trailing punctuation rides
    // along (e.g. "mortality.") — trim only the edges; interior separators survive
    // (`1.5`, `covid-19`, `mg/dl`).
    const token = rawToken.replace(/^[./-]+|[./-]+$/g, '');
    if (!token) continue;
    if (hasDigit(token)) {
      numbers.add(token);
      terms.add(token);
      words.push(token);
      continue;
    }
    if (token.length < 3 && !UNITS.has(token)) continue;
    if (STOPWORDS.has(token)) continue;
    terms.add(token);
    words.push(token);
  }
  return { terms, numbers, words };
};

const intersectionSize = (a: Set<string>, b: Set<string>): number => {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  let count = 0;
  for (const value of small) if (large.has(value)) count += 1;
  return count;
};

/** Longest run of consecutive shared words (word-level longest common substring). */
const longestSharedPhrase = (a: string[], b: string[]): number => {
  if (a.length === 0 || b.length === 0) return 0;
  let best = 0;
  const prev = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i += 1) {
    let diag = 0;
    for (let j = 1; j <= b.length; j += 1) {
      const above = prev[j] ?? 0;
      if (a[i - 1] === b[j - 1]) {
        const run = diag + 1;
        prev[j] = run;
        if (run > best) best = run;
      } else {
        prev[j] = 0;
      }
      diag = above;
    }
  }
  return best;
};

/**
 * Pick the candidate best supporting `claim`. Deterministic:
 * `intersection/√(|claim|·|cand|) + sharedNumbers·0.25 + longestSharedPhrase·0.05`;
 * highest wins, ties keep candidate order, all-zero → first candidate.
 */
export const selectCandidate = (
  claim: string,
  candidates: readonly CitationCandidateV1[]
): CitationCandidateV1 | undefined => {
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];

  const c = normalizeTerms(claim);
  let best = candidates[0];
  let bestScore = -1;
  for (const candidate of candidates) {
    const t = normalizeTerms(candidate.text);
    const denom = Math.sqrt(c.terms.size * t.terms.size);
    const overlap = denom > 0 ? intersectionSize(c.terms, t.terms) / denom : 0;
    const sharedNumbers = intersectionSize(c.numbers, t.numbers);
    const phrase = longestSharedPhrase(c.words, t.words);
    const score = overlap + sharedNumbers * 0.25 + phrase * 0.05;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
};
